import React, { useState } from 'react';
import logger from '../../utils/logger';
import { 
  Tag, 
  Plus, 
  X, 
  User, 
  Building, 
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  DollarSign,
  Users,
  Briefcase,
  Target,
  Calendar,
  MessageSquare,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const ContactManager = ({ contact, onUpdate, defaultTab = 'profile' }) => {
  const { updateLeadContact } = useData();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [newTag, setNewTag] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', or null
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Initialize contact data with proper defaults to avoid controlled/uncontrolled component warnings
  const [contactData, setContactData] = useState({
    // Individual Information
    email: contact?.email || '',
    firstName: contact?.first_name || '',
    lastName: contact?.last_name || '',
    phone: contact?.phone || '',
    jobTitle: contact?.job_title || '',
    linkedin: contact?.linkedin || '',
    
    // Company Information
    company: contact?.company || '',
    industry: contact?.industry || '',
    companySize: contact?.company_size || '',
    website: contact?.website || '',
    location: contact?.location || '',
    annualRevenue: contact?.annual_revenue || '',
    
    // Lead Information
    leadScore: contact?.score || 0,
    status: contact?.status || 'new',
    source: contact?.campaign_name || 'Direct',
    tags: Array.isArray(contact?.tags) ? contact.tags : ['Lead', 'Prospect'],
    
    // Notes and Activities
    notes: Array.isArray(contact?.notes) ? contact.notes : [],
    lastActivity: contact?.last_event_date || null
  });

  const handleAddTag = () => {
    if (newTag.trim() && !contactData.tags.includes(newTag.trim())) {
      const updatedData = {
        ...contactData,
        tags: [...contactData.tags, newTag.trim()]
      };
      setContactData(updatedData);
      setHasUnsavedChanges(true);
      setSaveStatus(null);
      onUpdate?.(updatedData);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const updatedData = {
      ...contactData,
      tags: contactData.tags.filter(tag => tag !== tagToRemove)
    };
    setContactData(updatedData);
    setHasUnsavedChanges(true);
    setSaveStatus(null);
    onUpdate?.(updatedData);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const note = {
        id: Date.now(),
        type: noteType,
        content: newNote.trim(),
        timestamp: new Date().toISOString(),
        author: 'Current User' // In real app, get from auth context
      };
      const updatedData = {
        ...contactData,
        notes: [note, ...contactData.notes]
      };
      setContactData(updatedData);
      setHasUnsavedChanges(true);
      setSaveStatus(null);
      onUpdate?.(updatedData);
      setNewNote('');
    }
  };

  const handleFieldUpdate = (field, value) => {
    const updatedData = {
      ...contactData,
      [field]: value
    };
    setContactData(updatedData);
    setHasUnsavedChanges(true);
    setSaveStatus(null);
    onUpdate?.(updatedData);
  };

  const handleSaveContact = async () => {
    if (!contact?.id) {
      setSaveStatus('error');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const result = await updateLeadContact(contact.id, contactData);
      
      if (result.success) {
        setSaveStatus('success');
        setHasUnsavedChanges(false);
        // Auto-hide success message after 3 seconds
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      logger.error('Error saving contact:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Contact Profile', icon: User },
    { id: 'company', label: 'Company Info', icon: Building },
    { id: 'tags', label: 'Tags & Notes', icon: Tag },
    { id: 'activity', label: 'Activity', icon: MessageSquare }
  ];

  const noteTypes = [
    { value: 'general', label: 'General Note' },
    { value: 'call', label: 'Call Note' },
    { value: 'meeting', label: 'Meeting Note' },
    { value: 'email', label: 'Email Note' },
    { value: 'follow-up', label: 'Follow-up' }
  ];

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Compact Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-900">Contact</h2>
        <div className="flex items-center space-x-2">
          {/* Save Status Indicator */}
          {saveStatus === 'success' && (
            <CheckCircle className="w-3 h-3 text-green-600" />
          )}
          {saveStatus === 'error' && (
            <AlertCircle className="w-3 h-3 text-red-600" />
          )}
          
          {/* Compact Save Button */}
          <button
            onClick={handleSaveContact}
            disabled={!hasUnsavedChanges || isSaving}
            className={`p-1.5 rounded-md text-xs transition-colors ${
              hasUnsavedChanges && !isSaving
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
            title={isSaving ? 'Saving...' : 'Save Changes'}
          >
            <Save className="w-3 h-3" />
          </button>
        </div>
      </div>
      
      {/* Compact Tab Navigation */}
      <div className="border-b border-gray-100">
        <nav className="flex px-2" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                } flex-1 py-2 px-1 border-b-2 font-medium text-xs flex items-center justify-center space-x-1 transition-all duration-200`}
                title={tab.label}
              >
                <Icon className="w-3 h-3" />
                <span className="hidden sm:inline">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Compact Tab Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {activeTab === 'profile' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2 mb-3">
              <User className="w-4 h-4" />
              <span>Contact Info</span>
            </h3>
            
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <div className="flex items-center space-x-2">
                  <Mail className="w-3 h-3 text-gray-400" />
                  <input
                    type="email"
                    value={contactData.email}
                    onChange={(e) => handleFieldUpdate('email', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                <input
                    type="text"
                    value={contactData.firstName || ''}
                    onChange={(e) => handleFieldUpdate('firstName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <input
                    type="text"
                    value={contactData.lastName || ''}
                    onChange={(e) => handleFieldUpdate('lastName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <div className="flex items-center space-x-2">
                  <Phone className="w-3 h-3 text-gray-400" />
                  <input
                    type="tel"
                    value={contactData.phone}
                    onChange={(e) => handleFieldUpdate('phone', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Job Title</label>
                <div className="flex items-center space-x-2">
                  <Briefcase className="w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    value={contactData.jobTitle || ''}
                    onChange={(e) => handleFieldUpdate('jobTitle', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., Marketing Manager"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">LinkedIn</label>
                <div className="flex items-center space-x-2">
                  <Target className="w-3 h-3 text-gray-400" />
                  <input
                    type="url"
                    value={contactData.linkedin || ''}
                     onChange={(e) => handleFieldUpdate('linkedin', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                    placeholder="linkedin.com/in/username"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                <div className="flex items-center space-x-2">
                  <Building className="w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    value={contactData.company || ''}
                    onChange={(e) => handleFieldUpdate('company', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={contactData.status || 'new'}
                  onChange={(e) => handleFieldUpdate('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="new">New</option>
                  <option value="interested">Interested</option>
                  <option value="call_list">Call List</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Lead Score</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={contactData.leadScore || 0}
                  onChange={(e) => handleFieldUpdate('leadScore', parseInt(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={contactData.source || ''}
                  onChange={(e) => handleFieldUpdate('source', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  placeholder="e.g., LinkedIn, Website, Referral"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Score</label>
                <div className="flex items-center space-x-1">
                  <Target className="w-3 h-3 text-gray-400" />
                  <span className="text-lg font-bold text-blue-600">{contactData.leadScore}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={contactData.status || 'new'}
                  onChange={(e) => handleFieldUpdate('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                >
                  <option value="new">New</option>
                  <option value="interested">Interested</option>
                  <option value="call_list">Call List</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  value={contactData.source || ''}
                  onChange={(e) => handleFieldUpdate('source', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'company' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2 mb-3">
              <Building className="w-4 h-4" />
              <span>Company Info</span>
            </h3>
            
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company Name</label>
                <div className="flex items-center space-x-2">
                  <Building className="w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    value={contactData.company || ''}
                    onChange={(e) => handleFieldUpdate('company', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Industry</label>
                <input
                  type="text"
                  value={contactData.industry || ''}
                  onChange={(e) => handleFieldUpdate('industry', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  placeholder="e.g., Technology"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company Size</label>
                <div className="flex items-center space-x-2">
                  <Users className="w-3 h-3 text-gray-400" />
                  <select
                    value={contactData.companySize || ''}
                    onChange={(e) => handleFieldUpdate('companySize', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Website</label>
                <div className="flex items-center space-x-2">
                  <Globe className="w-3 h-3 text-gray-400" />
                  <input
                    type="url"
                    value={contactData.website || ''}
                    onChange={(e) => handleFieldUpdate('website', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                    placeholder="company.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    value={contactData.location || ''}
                    onChange={(e) => handleFieldUpdate('location', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                    placeholder="City, Country"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Annual Revenue</label>
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-3 h-3 text-gray-400" />
                  <select
                    value={contactData.annualRevenue || ''}
                    onChange={(e) => handleFieldUpdate('annualRevenue', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select revenue</option>
                    <option value="<1M">Less than $1M</option>
                    <option value="1M-10M">$1M - $10M</option>
                    <option value="10M-50M">$10M - $50M</option>
                    <option value="50M-100M">$50M - $100M</option>
                    <option value="100M+">$100M+</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tags' && (
          <div className="space-y-3">
            {/* Tags Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <Tag className="w-4 h-4" />
                <span>Tags</span>
              </h3>
              
              <div className="flex flex-wrap gap-1 mb-3">
                {contactData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  placeholder="Add tag"
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                />
                <button
                  onClick={handleAddTag}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1"
                >
                  <Plus className="w-3 h-3" />
                  <span className="text-xs">Add</span>
                </button>
              </div>
            </div>

            {/* Notes Section */}
            <div className="border-t border-gray-100 pt-3">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>Notes</span>
              </h3>
              
              {/* Add Note Form */}
              <div className="bg-gray-50 rounded-md p-3 mb-3">
                <div className="mb-2">
                  <select
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                  >
                    {noteTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={2}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs mb-2"
                />
                <button
                  onClick={handleAddNote}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-1"
                >
                  <Plus className="w-3 h-3" />
                  <span className="text-xs">Add Note</span>
                </button>
              </div>
              
              {/* Notes List */}
              <div className="space-y-2">
                {contactData.notes.map((note) => (
                  <div key={note.id} className="bg-white border border-gray-200 rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {noteTypes.find(t => t.value === note.type)?.label || note.type}
                      </span>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(note.timestamp).toLocaleDateString()}</span>
                        <span>by {note.author}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700">{note.content}</p>
                  </div>
                ))}
                
                {contactData.notes.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-xs">No notes yet. Add your first note above.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2 mb-3">
              <MessageSquare className="w-4 h-4" />
              <span>Recent Activity</span>
            </h3>
            
            <div className="text-center py-6 text-gray-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-xs">Activity tracking will be implemented with lead events integration.</p>
              {contactData.lastActivity && (
                <p className="mt-2 text-xs">
                  Last activity: {new Date(contactData.lastActivity).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactManager;